import { useMutation, useQueryClient } from '@tanstack/react-query';
import { serverTimestamp, updateDoc } from 'firebase/firestore';
import { toast } from 'sonner';

import { useAuth } from '@/app/auth';
import { homeDoc } from '@/lib/firestorePaths';

export const HOME_NAME_MIN = 1;
export const HOME_NAME_MAX = 80;

interface UpdateHomeInput {
  id: string;
  name: string;
}

export function isValidHomeName(name: string): boolean {
  const trimmed = name.trim();
  return trimmed.length >= HOME_NAME_MIN && trimmed.length <= HOME_NAME_MAX;
}

export default function useUpdateHome() {
  const { user } = useAuth();
  const uid = user?.uid ?? '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, name }: UpdateHomeInput) => {
      const trimmed = name.trim();
      if (!isValidHomeName(trimmed)) {
        throw new Error(`Home name must be ${HOME_NAME_MIN}–${HOME_NAME_MAX} characters.`);
      }
      await updateDoc(homeDoc(id), {
        name: trimmed,
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: () => {
      toast.success('Home updated.');
      void queryClient.invalidateQueries({ queryKey: ['homeAutomation', 'currentHome', uid] });
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : 'Failed to update home.';
      toast.error(msg);
      void queryClient.invalidateQueries({ queryKey: ['homeAutomation', 'currentHome', uid] });
    },
  });
}
